import { useState, useEffect, lazy, Suspense } from "react";
import { Layout } from "providers/Layout";
import { Button, ButtonSize, ButtonVariant } from "shared/components";
import { BreadCrumbs } from "shared/components/BreadCrumbs";
import { Attachment02, Download01 } from "untitledui-js-base";
import { useIntercom } from "react-use-intercom";
import { DropZone } from "./components/DropZone";
import { useMutation, useQuery } from "@tanstack/react-query";
import { instance } from "api/apiBase";

import { getErrorMessage } from "utils/error";
import { useProgressContext } from "providers/ProgressContext";
import { Loader } from "shared/components/Loader";
import {
  CsvUploadStatus,
  CsvUploadStatusResponse
} from "providers/ProgressContext/slices/UploadCsvSlice/types";
import { batchUploadCrumbs } from "./constants";
import { Redirect } from "shared/components/Redirect";
import { Link, useLocation, useNavigate } from "react-router-dom";

import InfoIcon from "shared/icons/status-icons/info-icon.svg?react";
import { Card } from "./components/Card";
import { useAuthContext } from "providers/Auth";
import { isManualEnrollmentDisabledForPms } from "utils/manualEnrollmentRestrictions";
import { toast } from "react-toastify";
import { Tabs } from "shared/components/Tabs";
import { Table } from "shared/app/Table";
import { getCoreRowModel, useReactTable } from "@tanstack/react-table";
import { ContextMenuPopover } from "shared/components/ContextMenuPopover";
import { PaginatedResponse } from "shared/types";
import moment from "moment";

const AnalyzingReport = lazy(() => import("./slices/AnalyzingReport"));

enum BatchUploadTabs {
  Upload = "Upload",
  ProcessedFiles = "Processed files"
}

export const RentReportingUpload = () => {
  const { type, pms, permissions, partner } = useAuthContext();
  const intercom = useIntercom();
  const [file, setFile] = useState<File>();
  const { uploadCsv } = useProgressContext();
  const navigate = useNavigate();
  const location = useLocation();
  const [selectedTab, setSelectedTab] = useState<BatchUploadTabs>(
    location.state?.review
      ? BatchUploadTabs.ProcessedFiles
      : BatchUploadTabs.Upload
  );

  const csv_template_url = pms.isAppfolio
    ? "/appfolio_renters_template.csv"
    : "/renters_template.csv";

  const { mutate: analyzeCsv, isPending } = useMutation({
    mutationKey: ["UPLOAD_CSV"],
    mutationFn: () => {
      const formData = new FormData();
      formData.append("csv", file as Blob);

      return instance.post("portal/v1/csv", formData).then(() => {
        return uploadCsv.meta?.refetch();
      });
    },
    onError: (err) => {
      toast.error(getErrorMessage(err));
    }
  });

  // Pagination intentionally not wired up yet — full list fits current usage
  const uploadedFilesMeta = useQuery({
    queryKey: ["UPLOADED_FILES"],
    queryFn: () => {
      return instance
        .get<PaginatedResponse<CsvUploadStatusResponse>>("portal/v1/csv/list")
        .then(({ data }) => data);
    }
  });

  const table = useReactTable({
    getCoreRowModel: getCoreRowModel(),
    columns: [
      {
        header: "File name",
        accessorKey: "csv_filename",
        cell: (p) => {
          const csvItem = p.row.original as unknown as {
            id: number;
            csv_filename: string;
          };

          return (
            <Link
              to={`/rent-reporting/upload/${csvItem.id}`}
              className="text-content-1000 flex gap-2"
            >
              <Attachment02 className="text-content-100 h-5 w-5" />
              <span className="block text-content-1000 font-medium">
                {csvItem.csv_filename}
              </span>
            </Link>
          );
        }
      },
      {
        header: "Processed on",
        accessorKey: "updated_at",
        cell: (p) => {
          if (!p.getValue()) return "--";
          return moment(p.getValue()).format("ll");
        }
      },
      // Actions
      {
        id: "popover-action",
        header: "",
        size: 32,
        cell: (p) => {
          const csvItem = p.row.original as unknown as {
            id: number;
            csv_filename: string;
          };

          const actions = [
            {
              title: "Review",
              onClick: (e) => {
                e.stopPropagation();
                navigate(`/rent-reporting/upload/${csvItem.id}`);
              }
            },
            {
              title: "Download CSV analysis",
              onClick: (e) => {
                e.stopPropagation();
                navigate(`/rent-reporting/upload/${csvItem.id}`, {
                  state: {
                    print: true
                  }
                });
              }
            }
          ];

          return <ContextMenuPopover actions={actions} />;
        }
      }
    ],
    data: uploadedFilesMeta.data?.items || []
  });

  useEffect(() => {
    const cb = () => {
      navigate("/rent-reporting");
    };
    window.addEventListener("navigate-to-rent-reporting", cb);
    return () => {
      window.removeEventListener("navigate-to-rent-reporting", cb);
    };
  }, []);

  if (
    (type.isPMS && !type.isBoomOps && !type.canManageEntitiesDirectly) ||
    type.isSDK ||
    !permissions.canSubscribeRenters ||
    isManualEnrollmentDisabledForPms(partner?.property_management_system?.name)
  )
    return <Redirect href="/" />;

  if (
    !uploadCsv.meta ||
    uploadCsv.meta.isLoading ||
    uploadCsv.status === "loading"
  )
    return (
      <Layout
        headerClassName="pt-3"
        header={
          <div className="border-b border-alpha-100">
            <div className="mb-3 ml-6">
              <BreadCrumbs crumbs={batchUploadCrumbs} />
            </div>
          </div>
        }
      >
        <div className="flex h-full flex-col items-center justify-center">
          <Loader />
        </div>
      </Layout>
    );

  if (
    uploadCsv.canUpload ||
    uploadCsv.meta.data?.status === CsvUploadStatus.Parsed
  )
    return (
      <Layout
        headerClassName="pt-3"
        header={
          <div className="border-b border-alpha-100">
            <div className="mb-3 ml-6">
              <BreadCrumbs crumbs={batchUploadCrumbs} />
            </div>
          </div>
        }
        footer={
          <div className="flex justify-end gap-2">
            <Link to="/rent-reporting">
              <Button className="w-[125px]">Cancel</Button>
            </Link>
            <Button
              form="single-upload-form"
              variant={ButtonVariant.PRIMARY}
              onClick={() => {
                analyzeCsv();
              }}
              loading={isPending}
              disabled={
                isPending ||
                !file ||
                file.name.split(".").pop() !== "csv" ||
                // 2md
                file.size > 2097152
              }
              className="w-[125px]"
            >
              Continue
            </Button>
          </div>
        }
      >
        <div className="animate-appear mx-auto max-w-[760px]">
          <Card className="p-8">
            <div className="pb-6">
              <Tabs
                setActiveTab={(t) => setSelectedTab(t as BatchUploadTabs)}
                selected={selectedTab}
                tabs={["Upload", "Processed files"]}
                variant="underline"
              />
            </div>

            {selectedTab === BatchUploadTabs.Upload &&
            uploadCsv.meta.data?.status === CsvUploadStatus.Parsed ? (
              <div className="animate-appear">
                <h1 className="mb-6 text-lg font-bold text-content-1000">
                  CSV file analysis is ready to be reviewed
                </h1>

                <p className="mb-6 border-b border-alpha-40 pb-6 text-sm font-medium">
                  The file has been successfully parsed. Please review the
                  details below before proceeding.
                </p>

                <p className="mb-6 pb-6 text-sm font-medium">
                  New CSV upload is forbidden until the current one is processed
                </p>

                <Link to="/rent-reporting/upload/csv-analysis">
                  <Button
                    variant={ButtonVariant.PRIMARY}
                    onClick={() => {
                      setSelectedTab(BatchUploadTabs.ProcessedFiles);
                    }}
                    className="mt-6"
                  >
                    Review analysis
                  </Button>
                </Link>
              </div>
            ) : selectedTab === BatchUploadTabs.Upload ? (
              <div className="animate-appear">
                <h1 className="mb-6 text-lg font-bold text-content-1000">
                  Upload rent payments and subscribe/unsubscribe renters
                </h1>

                <p className="mb-6 border-b border-alpha-40 pb-6 text-sm font-medium">
                  Please download the template below to manage your renter. In
                  the next step, you can specify whether you want to subscribe
                  renters who are listed in the file, unsubscribe renters who
                  are missing from the file, or report rent payments. If you
                  need assistance, please{" "}
                  <Button
                    variant={ButtonVariant.TEXT}
                    onClick={() => {
                      intercom.show();
                    }}
                    className="!inline"
                  >
                    contact our team
                  </Button>{" "}
                </p>

                <div className="flex w-full gap-4 items-center mb-6 ">
                  <a
                    href={csv_template_url}
                    download="renters_template.csv"
                    className="flex w-fit"
                  >
                    <Button
                      size={ButtonSize.SM}
                      Icon={Download01}
                      disabled={false}
                    >
                      Download CSV template
                    </Button>
                  </a>

                  <p className="flex items-center gap-2 text-sm text-content-1000 font-medium">
                    <InfoIcon className="h-4 w-4 shrink-0 text-content-200" />{" "}
                    Please do not change the column headers in the CSV template
                  </p>
                </div>
                <DropZone
                  id="upload-csv"
                  title="Upload CSV"
                  value={file}
                  setValue={setFile}
                />
              </div>
            ) : (
              <div>
                <h1 className="mb-6 text-lg font-bold text-content-1000">
                  Processed files
                </h1>

                <p className="mb-6 pb-6 text-sm font-medium">
                  List of files that have been successfully processed
                </p>

                <Table
                  table={table}
                  borderLess
                  maw={720}
                  emptyRowsNumber={1}
                  emptyStateText="No CSVs uploaded"
                />
              </div>
            )}
          </Card>
        </div>
      </Layout>
    );
  if (
    uploadCsv.meta.data?.status === CsvUploadStatus.Parsing ||
    uploadCsv.meta.data?.status === CsvUploadStatus.Executing
  )
    return (
      <Suspense fallback={<Loader />}>
        <AnalyzingReport />
      </Suspense>
    );

  return <Redirect href="/rent-reporting" />;
};
